// ============================================
// Magic Link Service
//
// Client-side utilities for Firebase passwordless sign-in.
//
// Flow:
//   1. Patient enters email → sendMagicLink CF sends branded email
//   2. Email stored in localStorage (needed to complete sign-in on return)
//   3. Patient clicks link → lands on /r/:sessionId with auth params
//   4. handleMagicLinkReturn() completes sign-in + links journey to UID
// ============================================

import {
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions, resetAuthUid } from "../firebase";
import { linkJourneyToUid } from "../firestore/journeyService";

const MAGIC_LINK_EMAIL_KEY = "hemologix_magic_link_email";

// ── Send magic link ──

/**
 * Call the sendMagicLink Cloud Function.
 * Stores email in localStorage for the return trip.
 */
export async function requestMagicLink(
  email: string,
  sessionId: string,
): Promise<{ success: boolean; error?: string }> {
  const trimmed = email.trim().toLowerCase();

  try {
    const callable = httpsCallable(functions, "sendMagicLink");
    await callable({ email: trimmed, sessionId });

    // Store email for return trip (Firebase requires it to complete sign-in)
    try {
      localStorage.setItem(MAGIC_LINK_EMAIL_KEY, trimmed);
    } catch {
      // localStorage unavailable — user will need to re-enter email on return
    }

    return { success: true };
  } catch (err: unknown) {
    const message = (err as { message?: string }).message || "Failed to send link. Please try again.";
    console.error("[MagicLink] Send failed:", err);
    return { success: false, error: message };
  }
}

// ── Handle magic link return ──

/**
 * Check if the current URL is a Firebase magic link.
 * If so, complete sign-in and link the journey to the new UID.
 *
 * Returns the email if sign-in succeeded, null if not a magic link or failed.
 */
export async function handleMagicLinkReturn(
  sessionId: string,
): Promise<{ email: string } | null> {
  const url = window.location.href;

  if (!isSignInWithEmailLink(auth, url)) {
    return null;
  }

  // Get email from localStorage (saved when they entered it)
  let email = getSavedMagicLinkEmail();

  if (!email) {
    // If no email in localStorage (different device/browser), ask user
    // The caller should handle this case with a prompt
    return null;
  }

  try {
    const result = await signInWithEmailLink(auth, email, url);
    resetAuthUid();

    // Link the journey to the authenticated UID
    linkJourneyToUid(sessionId, result.user.uid).catch((err) =>
      console.warn("[MagicLink] Journey link failed:", err),
    );

    // Clean up
    clearSavedMagicLinkEmail();

    // Clean URL params (remove oobCode etc.)
    if (window.history.replaceState) {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState(null, "", cleanUrl);
    }

    return { email: result.user.email || email };
  } catch (err: unknown) {
    const code = (err as { code?: string }).code;
    console.error("[MagicLink] Sign-in failed:", code, err);

    // Invalid or expired link
    if (code === "auth/invalid-action-code" || code === "auth/expired-action-code") {
      clearSavedMagicLinkEmail();
    }

    return null;
  }
}

// ── Email storage helpers ──

export function getSavedMagicLinkEmail(): string | null {
  try {
    return localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
  } catch {
    return null;
  }
}

export function clearSavedMagicLinkEmail(): void {
  try {
    localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
  } catch {
    // noop
  }
}
